import { Button, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { confirmDelete } from '../lib/confirm';

/** `DELETE /staff/:id` to'sib qo'yganda serverdan keladigan javob. */
interface Bloklovchi { jadval: string; nom: string; soni: number }
interface OchirishXatosi { error?: string; bloklaydi?: Bloklovchi[] }

/**
 * Foydalanuvchini BUTUNLAY o'chirish tugmasi — xodim, o'qituvchi va ota-ona
 * kartochkalari uchun bitta. Backend ham bitta: `DELETE /staff/:id` roldan qat'i
 * nazar ishlaydi (to'sqinliklar `User` jadvaliga bog'langan, rolga emas).
 *
 * Asosiy yo'l bu emas: odatda xodim FAOLSIZLANTIRILADI (`isActive=false`), shunda
 * uning baholari, to'lovlari va ish tarixi joyida qoladi. Shuning uchun tugma
 * ko'zga tashlanmaydigan `subtle` ko'rinishda.
 *
 * Server bog'liq yozuv topsa `409` va NIMA to'sayotgani ro'yxatini qaytaradi —
 * u shu yerda ochiq ko'rsatiladi. Aks holda xodim "nega o'chmadi" degan savolga
 * javob olmasdi: Prisma'ning tashqi kalit xatosi tushunarsiz matn beradi.
 */
export function StaffDeleteButton({
  id,
  fullName,
  backTo,
  /** O'chgandan keyin yangilanadigan qo'shimcha kesh kalitlari (masalan ota-onalar ro'yxati). */
  invalidateKeys = [],
}: {
  id: string;
  fullName: string;
  backTo: string;
  invalidateKeys?: string[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const mut = useMutation({
    mutationFn: () => api.delete(`/staff/${id}`),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('staffDelete.done') });
      qc.invalidateQueries({ queryKey: ['staff-all'] });
      qc.invalidateQueries({ queryKey: ['teachers-page'] });
      qc.invalidateQueries({ queryKey: ['teachers'] });
      invalidateKeys.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      navigate(backTo);
    },
    onError: (e: unknown) => {
      const r = (e as { response?: { status?: number; data?: OchirishXatosi } })?.response;
      if (r?.status === 409 && r.data?.bloklaydi?.length) return bloklanganiniKorsat(r.data.bloklaydi);
      notifications.show({ color: 'red', message: r?.data?.error ?? t('common.error') });
    },
  });

  /* To'sqinlik — bu XATO emas, balki kutilgan holat: xodim ishlagan, izi bor.
     Shuning uchun ogohlantirish emas, tushuntirish ko'rsatiladi. */
  const bloklanganiniKorsat = (rows: Bloklovchi[]) =>
    modals.open({
      title: t('staffDelete.blockedTitle'),
      centered: true,
      children: (
        <Stack gap="xs">
          <Text size="sm">{t('staffDelete.blockedIntro', { name: fullName })}</Text>
          {rows.map((r) => (
            <Text key={`${r.jadval}-${r.nom}`} size="sm">• {r.nom}: <b>{r.soni}</b></Text>
          ))}
          <Text size="sm" c="dimmed">{t('staffDelete.blockedHint')}</Text>
        </Stack>
      ),
    });

  return (
    <Button
      variant="subtle"
      color="red"
      leftSection={<IconTrash size={16} />}
      loading={mut.isPending}
      onClick={() => confirmDelete(fullName, () => mut.mutate())}
    >
      {t('staffDelete.button')}
    </Button>
  );
}
